/**
 * DB reassembly seams for the runner's content corpora.
 *
 * The database is the single source of truth. Each builder narrows the opaque `CampaignContentBlock` /
 * `CampaignCapability` rows the portal injected (via `resolveContentBlocks` / `resolveCapabilities`)
 * back into the exact TypeScript shapes the runner renders — the shapes' TYPES still live in
 * `config/content.ts` and `config/deepDive.ts`, but the DATA comes only from the DB. There is NO bundled
 * fallback: when a category has no blocks a builder returns an empty result, and `GuidedRunner` shows a
 * "not configured" panel rather than rendering half a workshop from stale bundled data (see the note on
 * the removed fallback in `config/content.ts`).
 *
 * The one field that cannot come from the DB is a gap row's `liveCurrentState`: it is a function
 * (behavior, not content), so it is supplied from `config/liveFigures.ts` keyed by the row `slug`. See
 * `resolveGapRows`.
 *
 * Decks/files are a separate, asset-backed subsystem (`resolveAssets`, S3-backed uploads): those
 * builders still overlay onto the bundled `DECK_SOURCES` so every `DeckId` key is always present and a
 * standalone deployment keeps working. That is intentional and unrelated to the content-block corpora.
 */
import {
  recommendedKey as _recommendedKey,
  type AgendaItem,
  type BankQuestionRef,
  type DeepDivePillarQuestion,
  type GapRowDef,
  type IqScenario,
  type IqScenarioDef,
  type QuestionGroup,
  type RecommendedQuestion,
  type StakeholderPriorityMap,
} from '../config/content';
import {
  type ClosingItem,
  type ClosingMap,
  type DeepDiveBand,
  type DeepDiveBandGuide,
  type DeepDivePhase,
  type DeepDivePlay,
  type DeepDiveQuestion,
  type DeepDiveScenario,
  type DeepDiveSection,
  type DeepDiveStakeholder,
  type PushbackItem,
  type SequencingRow,
} from '../config/deepDive';
import { DECK_SOURCES, type DeckSourceConfig } from '../config/decks/sources';
import { LIVE_CURRENT_STATE_BY_SLUG } from '../config/liveFigures';
import type { StepKind } from '../config/steps';
import type { DeckId } from '../modules/decks/generate';
import type { SourceDeck } from '../modules/decks/sourceDeck';
import {
  getRunnerData,
  resolveAssets,
  resolveCapabilities,
  resolveContentBlocks,
  type RunnerAsset,
  type RunnerContentBlock,
} from './campaignData';

/** One category's blocks, in the `order` the seed assigned. */
function blocksByOrder(category: string): RunnerContentBlock[] {
  return [...resolveContentBlocks(category)].sort((a, b) => a.order - b.order);
}

// ---------------------------------------------------------------------------
// content.ts corpora
// ---------------------------------------------------------------------------

export interface ConnectToolCard {
  key: string;
  name: string;
  url: string;
  linkNote?: string;
  howTo?: string[];
  /** Heading over `howTo` — defaults to "How to check" when absent. */
  howToTitle?: string;
  confirm?: {
    idle: string;
    done: string;
    on: string;
    off: string;
  };
  deny?: {
    idle: string;
    done: string;
    on: string;
  };
}

/**
 * Connect tool cards for the Profiling step. Each block's payload carries the card configuration;
 * the confirm/deny button label sets are assembled from individual label fields when present.
 */
export function resolveConnectTools(): ConnectToolCard[] {
  return blocksByOrder('connect_tool').map((b) => {
    const p = b.payload ?? {};
    const card: ConnectToolCard = {
      key: b.key,
      name: String(p.name ?? ''),
      url: String(p.url ?? ''),
    };
    if (p.linkNote) card.linkNote = String(p.linkNote);
    if (Array.isArray(p.howTo) && p.howTo.length > 0) card.howTo = p.howTo as string[];
    if (p.howToTitle) card.howToTitle = String(p.howToTitle);
    // Assemble confirm object if any label is present.
    if (p.confirmIdleLabel || p.confirmDoneLabel || p.confirmOnLabel || p.confirmOffLabel) {
      card.confirm = {
        idle: String(p.confirmIdleLabel ?? ''),
        done: String(p.confirmDoneLabel ?? ''),
        on: String(p.confirmOnLabel ?? ''),
        off: String(p.confirmOffLabel ?? ''),
      };
    }
    // Assemble deny object if any label is present.
    if (p.denyIdleLabel || p.denyDoneLabel || p.denyOnLabel) {
      card.deny = {
        idle: String(p.denyIdleLabel ?? ''),
        done: String(p.denyDoneLabel ?? ''),
        on: String(p.denyOnLabel ?? ''),
      };
    }
    return card;
  });
}

/**
 * The Workshop Guide's proactive nudge copy, from the DB `nudge` blocks. Each block's `key` names the
 * slot: the singletons `welcome` / `liveexit` / `idle`; the per-step families
 * `intro:<kind>` / `videodone:<kind>` / `live:<kind>` (keyed by `StepKind`); and `phasedone:<phase>`
 * (keyed by the step's `phase`). The nudge text is `payload.text`. `StepPage` looks each up by the
 * step's kind / phase and fires it only when the DB has copy for that slot — there is no bundled
 * fallback.
 */
export interface RunnerNudges {
  welcome?: string;
  liveExit?: string;
  idle?: string;
  /** Per-step nudges keyed by `StepKind`. */
  stepIntro: Record<string, string>;
  videoDone: Record<string, string>;
  live: Record<string, string>;
  /** Keyed by the step's `phase`. */
  phaseDone: Record<string, string>;
}

export function resolveNudges(): RunnerNudges {
  const out: RunnerNudges = { stepIntro: {}, videoDone: {}, live: {}, phaseDone: {} };
  for (const b of blocksByOrder('nudge')) {
    const text = typeof b.payload?.text === 'string' ? (b.payload.text as string) : '';
    if (!text) continue;
    const sep = b.key.indexOf(':');
    const type = sep === -1 ? b.key : b.key.slice(0, sep);
    const sub = sep === -1 ? '' : b.key.slice(sep + 1);
    switch (type) {
      case 'welcome':
        out.welcome = text;
        break;
      case 'liveexit':
        out.liveExit = text;
        break;
      case 'idle':
        out.idle = text;
        break;
      case 'intro':
        if (sub) out.stepIntro[sub] = text;
        break;
      case 'videodone':
        if (sub) out.videoDone[sub] = text;
        break;
      case 'live':
        if (sub) out.live[sub] = text;
        break;
      case 'phasedone':
        if (sub) out.phaseDone[sub] = text;
        break;
    }
  }
  return out;
}

/**
 * `GAP_ROWS`, from the DB `gap_row` blocks. The scalar text comes from the block payload; the
 * `liveCurrentState` function alone is supplied from `config/liveFigures.ts` matched by `slug`
 * (behavior cannot live in JSON). A slug with no registered function gets `() => null`.
 */
export function resolveGapRows(): GapRowDef[] {
  return blocksByOrder('gap_row').map((b) => {
    const p = b.payload ?? {};
    const slug = String(p.slug ?? b.key);
    return {
      slug,
      category: String(p.category ?? ''),
      currentState: String(p.currentState ?? ''),
      liveCurrentState: LIVE_CURRENT_STATE_BY_SLUG[slug] ?? (() => null),
      gap: String(p.gap ?? ''),
      action: String(p.action ?? ''),
      defaultStatus: (p.defaultStatus ?? 'notstarted') as GapRowDef['defaultStatus'],
    };
  });
}

export function resolveAgenda(): AgendaItem[] {
  return blocksByOrder('agenda').map((b) => b.payload as AgendaItem);
}

/** Derived from `resolveAgenda`, exactly as the bundled `AGENDA_TOTAL` was derived from `AGENDA`. */
export function resolveAgendaTotal(): string {
  return `Allow about ${resolveAgenda().reduce(
    (total, row) => total + (parseInt(row.duration, 10) || 0),
    0,
  )} minutes in total.`;
}

export function resolveNextSteps(): string[] {
  return blocksByOrder('next_step').map((b) => String(b.payload?.text ?? ''));
}

export function resolveDiscoveryQuestions(): string[] {
  return blocksByOrder('discovery_question').map((b) => String(b.payload?.text ?? ''));
}

export function resolvePrereqQuestions(): string[] {
  return blocksByOrder('prereq_question').map((b) => String(b.payload?.text ?? ''));
}

export function resolveStakeholderPriority(): StakeholderPriorityMap {
  const blocks = resolveContentBlocks('stakeholder_priority');
  return (blocks[0]?.payload as StakeholderPriorityMap) ?? { must: '', should: '', nice: '' };
}

export function resolveIqScenarios(): IqScenarioDef[] {
  return blocksByOrder('iq_scenario').map((b) => b.payload as IqScenarioDef);
}

export function resolveRecommendedQuestions(): RecommendedQuestion[] {
  return blocksByOrder('recommended_question').map((b) => b.payload as RecommendedQuestion);
}

export function resolveDeepDiveQuestions(): DeepDivePillarQuestion[] {
  return blocksByOrder('deep_dive_question').map((b) => b.payload as DeepDivePillarQuestion);
}

export function resolveQuestionBank(): QuestionGroup[] {
  return blocksByOrder('question_bank').map((b) => b.payload as QuestionGroup);
}

/** Derived from `resolveQuestionBank`, exactly as bundled `ALL_BANK_QUESTIONS` was from `QUESTION_BANK`. */
export function resolveAllBankQuestions(): BankQuestionRef[] {
  return resolveQuestionBank().flatMap((group) =>
    group.questions.map((question, index) => ({
      key: `${group.id}.${index}`,
      question,
      group: group.label,
    })),
  );
}

/**
 * `scenariosFor`, reading DB-backed scenarios. Same logic as the bundled function; the "nobody has
 * checked yet" branch enumerates `resolveIqScenarios()`.
 */
export function resolveScenariosFor(iq: { connected: boolean; declined?: boolean }): IqScenario[] {
  if (iq.declined) return ['not-enabled'];
  if (iq.connected) return ['active', 'enabled-unused'];
  return resolveIqScenarios().map((scenario) => scenario.id);
}

// ---------------------------------------------------------------------------
// Decks (asset-backed; bundled overlay retained — see file header)
// ---------------------------------------------------------------------------

/**
 * `DECK_SOURCES`, rebuilt from the `deck`-kind assets. Each asset's `metadata` carries the bundled
 * `{ kicker, images, file, deck }`; `deck` is the full `SourceDeck` (pure data, so it round-trips
 * faithfully). Overlays onto a copy of the bundled map so every `DeckId` key is always present, and
 * falls back to bundled entirely when no deck assets exist.
 *
 * Each rebuilt entry also carries the asset's `assetId` and uploaded `objectKey`, so a consumer can
 * prefer a short-lived signed GET for the uploaded file (via `getCampaignAssetDownload`) over the
 * bundled public `metadata.file`. Full-screen slide rendering still reads `metadata.images` — an
 * arbitrary uploaded binary is not rendered as slides (that needs the separate PPTX→image pipeline).
 */
export function resolveDeckSources(): Record<DeckId, DeckSourceConfig> {
  const assets = resolveAssets('deck');
  if (!assets.length) return DECK_SOURCES;
  const out: Record<DeckId, DeckSourceConfig> = { ...DECK_SOURCES };
  for (const a of assets) {
    const m = a.metadata ?? {};
    (out as Record<string, DeckSourceConfig>)[a.key] = {
      deck: m.deck as SourceDeck,
      kicker: String(m.kicker ?? ''),
      images: m.images != null ? String(m.images) : undefined,
      file: m.file != null ? String(m.file) : undefined,
      assetId: a.id,
      objectKey: a.objectKey || undefined,
    };
  }
  return out;
}

/**
 * The deck a given step should present, resolved from its declared file reference rather than a
 * hard-coded id. Reads the step's injected content (`fileRefs`) for a `deck`-slot asset key and
 * returns that rebuilt `DeckSourceConfig`; falls back to the hard-coded `fallbackDeckId` entry when
 * the step declares no deck reference (the seeded/standalone path) or the referenced key is unknown.
 * Always returns a config, never undefined.
 */
export function resolveDeckSourceForStep(kind: StepKind, fallbackDeckId: DeckId): DeckSourceConfig {
  const sources = resolveDeckSources();
  const content = getRunnerData()?.stepContent?.find((c) => c.kind === kind);
  const ref = content?.fileRefs?.find((r) => r.slot === 'deck');
  const byRef = ref?.assetKey
    ? (sources as Record<string, DeckSourceConfig>)[ref.assetKey]
    : undefined;
  return byRef ?? sources[fallbackDeckId];
}

/**
 * The author-set, end-user-facing heading for a step's file `slot` (the `label` on its `fileRefs`),
 * or undefined when the step declares no such ref or left the label blank. Lets a step body render an
 * author-customizable file heading (e.g. the workshop deck's section title) in place of its bundled
 * default. Reads the injected `stepContent` exactly like `resolveDeckSourceForStep`.
 */
export function resolveFileLabelForStep(kind: StepKind, slot: string): string | undefined {
  const content = getRunnerData()?.stepContent?.find((c) => c.kind === kind);
  const label = content?.fileRefs?.find((r) => r.slot === slot)?.label;
  return label && label.trim() ? label.trim() : undefined;
}

/**
 * The raw `CampaignAsset` a given step declares for a named `slot` — the generic counterpart to
 * `resolveDeckSourceForStep` for non-deck files (e.g. the Propose step's `email` template and
 * `onepager`). Reads the step's injected `fileRefs` for a ref whose `slot` matches, then returns that
 * asset (matched by `assetKey` against `resolveAssets()`), so a consumer can prefer a short-lived
 * signed GET of the uploaded file (`getCampaignAssetDownload(asset.id)`) over a bundled public path.
 * Returns undefined when the step declares no such ref or the referenced key is unknown/unuploaded —
 * the caller then falls back to its bundled static file (today's behavior).
 */
export function resolveAssetForStep(kind: StepKind, slot: string): RunnerAsset | undefined {
  const content = getRunnerData()?.stepContent?.find((c) => c.kind === kind);
  const ref = content?.fileRefs?.find((r) => r.slot === slot);
  if (!ref?.assetKey) return undefined;
  const asset = resolveAssets().find((a) => a.key === ref.assetKey);
  // Only worth returning when it actually points at an uploaded object — otherwise the bundled
  // static fallback is the right source.
  return asset && asset.objectKey ? asset : undefined;
}

/**
 * The predefined question blocks a given step should present, resolved from its declared question
 * references rather than a whole-category read. Reads the step's injected content (`questionRefs`) via
 * `getRunnerData()` — exactly like `resolveDeckSourceForStep` reads `fileRefs` — and resolves each
 * ref to its `CampaignContentBlock` (matched within its `category` by `key`), preserving the authored
 * order of the refs. Falls back to the whole-category read `blocksByOrder(fallbackCategory)` (still a
 * DB read, never bundled) when the step declares no question refs or the refs resolve to nothing.
 */
export function resolveQuestionsForStep(
  kind: StepKind,
  fallbackCategory: string,
): RunnerContentBlock[] {
  const content = getRunnerData()?.stepContent?.find((c) => c.kind === kind);
  const refs = content?.questionRefs;
  if (!refs || !refs.length) return blocksByOrder(fallbackCategory);
  const resolved: RunnerContentBlock[] = [];
  for (const ref of refs) {
    if (!ref?.category || !ref?.key) continue;
    const block = resolveContentBlocks(ref.category).find((b) => b.key === ref.key);
    if (block) resolved.push(block);
  }
  // If the refs resolved to nothing (stale keys against a mismatched DB), fall back to the whole
  // category rather than rendering an empty question set.
  return resolved.length ? resolved : blocksByOrder(fallbackCategory);
}

// ---------------------------------------------------------------------------
// Deep dive
// ---------------------------------------------------------------------------

export function resolveDeepDiveStakeholders(): DeepDiveStakeholder[] {
  return blocksByOrder('deep_dive_stakeholder').map((b) => b.payload as DeepDiveStakeholder);
}

/** The deep-dive play catalogue, from the shared capability corpus (`{ id: key, name, detail }`). */
export function resolveDeepDivePlays(): DeepDivePlay[] {
  return resolveCapabilities().map((c) => ({ id: c.key, name: c.name, detail: c.detail }));
}

/** `playById`, from the resolved play catalogue. */
export function resolvePlayById(): Map<string, DeepDivePlay> {
  return new Map(resolveDeepDivePlays().map((p) => [p.id, p]));
}

/**
 * A question's `bands` map, resilient to re-authoring. When the payload still carries the seeded
 * `bands` shape we use it verbatim. When a question was re-saved via the admin ContentBlocks editor,
 * only the canonical `options` list survives — `[{ value: 'gap' | 'partial' | 'covered', cues, plays }]`,
 * one entry per band, exactly as migration 0137's `_band_options` wrote it — and `payload.bands` is
 * absent. In that case we reconstruct the `Record<DeepDiveBand, DeepDiveBandGuide>` from `options`, so
 * the cue chips never vanish. Any band an option omits falls back to empty cue/play lists, so the
 * record is always total.
 */
function bandsFromPayload(qp: Record<string, unknown>): DeepDiveQuestion['bands'] {
  if (qp.bands != null) return qp.bands as DeepDiveQuestion['bands'];
  const bands: Record<DeepDiveBand, DeepDiveBandGuide> = {
    gap: { cues: [], plays: [] },
    partial: { cues: [], plays: [] },
    covered: { cues: [], plays: [] },
  };
  const options = Array.isArray(qp.options) ? (qp.options as Array<Record<string, unknown>>) : [];
  for (const opt of options) {
    const value = String(opt?.value ?? '');
    if (value === 'gap' || value === 'partial' || value === 'covered') {
      bands[value] = {
        cues: Array.isArray(opt.cues) ? (opt.cues as string[]) : [],
        plays: Array.isArray(opt.plays) ? (opt.plays as string[]) : [],
      };
    }
  }
  return bands;
}

/** The flat section catalogue: `scenario_section` blocks + their `scenario_question` children. */
export function resolveDeepDiveSections(): DeepDiveSection[] {
  const secBlocks = blocksByOrder('scenario_section');
  const qBlocks = blocksByOrder('scenario_question');
  return secBlocks.map((sb) => {
    const p = sb.payload ?? {};
    const questions: DeepDiveQuestion[] = qBlocks
      .filter((q) => String(q.parentId) === String(sb.id))
      .map((q) => {
        const qp = q.payload ?? {};
        return {
          id: q.key,
          ask: String(qp.ask ?? ''),
          who: (qp.who ?? []) as DeepDiveQuestion['who'],
          ...(qp.why != null ? { why: String(qp.why) } : {}),
          bands: bandsFromPayload(qp),
        };
      });
    return {
      id: sb.key,
      code: String(p.code ?? ''),
      group: String(p.group ?? ''),
      title: sb.label,
      short: String(p.short ?? ''),
      why: String(p.why ?? ''),
      questions,
    };
  });
}

/** The three scenarios: `scenario` blocks + `scenario_phase` children whose `sections` are section ids. */
export function resolveDeepDiveScenarios(): DeepDiveScenario[] {
  const scenBlocks = blocksByOrder('scenario');
  const phaseBlocks = blocksByOrder('scenario_phase');
  const sectionById = new Map(resolveDeepDiveSections().map((s) => [s.id, s]));
  return scenBlocks.map((sb) => {
    const p = sb.payload ?? {};
    const phases = phaseBlocks
      .filter((ph) => String(ph.parentId) === String(sb.id))
      .map((ph) => {
        const pp = ph.payload ?? {};
        const ids = (pp.sections ?? []) as string[];
        return {
          id: ph.key.split(':')[1] as DeepDivePhase,
          n: String(pp.n ?? ''),
          label: ph.label,
          lead: String(pp.lead ?? ''),
          sections: ids
            .map((id) => sectionById.get(id))
            .filter((s): s is DeepDiveSection => Boolean(s)),
        };
      });
    return {
      id: sb.key as DeepDiveScenario['id'],
      n: String(p.n ?? ''),
      title: sb.label,
      sub: String(p.sub ?? ''),
      setup: String(p.setup ?? ''),
      phases,
    };
  });
}

/** `scenarioById`, from the resolved scenarios. */
export function resolveScenarioById(): Map<DeepDiveScenario['id'], DeepDiveScenario> {
  return new Map(resolveDeepDiveScenarios().map((s) => [s.id, s]));
}

export function resolveSequencing(): SequencingRow[] {
  return blocksByOrder('sequencing')
    .filter((b) => b.key !== 'fallback')
    .map((b) => b.payload as SequencingRow);
}

export function resolveSequencingFallback(): string {
  const fb = resolveContentBlocks('sequencing').find((b) => b.key === 'fallback');
  return String(fb?.payload?.text ?? '');
}

export function resolveClosing(): ClosingMap {
  const out: ClosingMap = { assess: [], act: [], optimize: [] };
  for (const b of blocksByOrder('closing')) {
    const p = b.payload ?? {};
    const phase = p.phase as DeepDivePhase;
    if (!out[phase]) continue;
    const item: ClosingItem = { title: String(p.title ?? ''), body: String(p.body ?? '') };
    out[phase].push(item);
  }
  return out;
}

export function resolvePushback(): PushbackItem[] {
  return blocksByOrder('pushback').map((b) => b.payload as PushbackItem);
}
